import { Locale } from '../../types'
import { TipoLocaleBadge, FasciaPrezzoBadge, ParcheggioGrandeBadge, InEvidenzaBadge } from '../common/Badge'
import FavoritoButton from './FavoritoButton'
import { getImageUrl } from '../../utils/image'

interface LocaleDetailProps {
  locale: Locale
  isFavorito?: boolean
  onFavoritoClick?: () => void
}

/**
 * Visualizzazione completa di un locale
 * Include tutte le informazioni, contatti, orari, galleria
 */
export default function LocaleDetail({
  locale,
  isFavorito = false,
  onFavoritoClick,
}: LocaleDetailProps) {
  // Formatta gli orari per la visualizzazione
  const giorniSettimana = [
    { key: 'lunedi', label: 'Lunedì' },
    { key: 'martedi', label: 'Martedì' },
    { key: 'mercoledi', label: 'Mercoledì' },
    { key: 'giovedi', label: 'Giovedì' },
    { key: 'venerdi', label: 'Venerdì' },
    { key: 'sabato', label: 'Sabato' },
    { key: 'domenica', label: 'Domenica' },
  ] as const

  return (
    <article className="space-y-8">
      {/* Hero immagine */}
      <div className="relative rounded-xl overflow-hidden">
        <div className="aspect-[21/9] md:aspect-[3/1]">
          <img
            src={getImageUrl(locale.fotoPrincipale)}
            alt={locale.nome}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=600&fit=crop'
            }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/50 to-transparent" />

        {/* Badge e azioni sull'immagine */}
        <div className="absolute top-4 left-4 flex gap-2">
          {locale.inEvidenza && <InEvidenzaBadge size="md" />}
          {locale.parcheggioGrande && <ParcheggioGrandeBadge size="md" />}
        </div>
        <div className="absolute top-4 right-4">
          <FavoritoButton
            isFavorito={isFavorito}
            onClick={onFavoritoClick}
            size="lg"
          />
        </div>
      </div>

      {/* Contenuto principale */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Colonna principale */}
        <div className="lg:col-span-2 space-y-8">
          {/* Header */}
          <header>
            <div className="flex flex-wrap gap-2 mb-3">
              <TipoLocaleBadge tipo={locale.tipo} size="md" />
              {locale.fasciaPrezzo && (
                <FasciaPrezzoBadge fascia={locale.fasciaPrezzo} size="md" />
              )}
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-white mb-2">
              {locale.nome}
            </h1>
            <p className="text-lg text-gray-400 flex items-center gap-2">
              <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {locale.indirizzo}, {locale.comune} ({locale.provincia})
            </p>
          </header>

          {/* Descrizione */}
          {locale.descrizione && (
            <section>
              <h2 className="font-display text-2xl text-white mb-4">Descrizione</h2>
              <p className="text-gray-300 leading-relaxed">{locale.descrizione}</p>
            </section>
          )}

          {/* Perché approvato */}
          {locale.percheApprovato && (
            <section className="bg-gold/10 border border-gold/20 rounded-xl p-6">
              <h2 className="font-display text-2xl text-gold mb-4 flex items-center gap-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Perché è Approved
              </h2>
              <p className="text-gray-300 leading-relaxed">{locale.percheApprovato}</p>
            </section>
          )}

          {/* Specialità */}
          {locale.specialita.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-white mb-4">Specialità</h2>
              <div className="flex flex-wrap gap-2">
                {locale.specialita.map((spec, i) => (
                  <span
                    key={i}
                    className="bg-night-light border border-gray-700 text-gray-300 px-4 py-2 rounded-full"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Galleria */}
          {locale.galleria && locale.galleria.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-white mb-4">Galleria</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {locale.galleria.map((img, i) => (
                  <a
                    key={i}
                    href={getImageUrl(img)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-video rounded-lg overflow-hidden group"
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`${locale.nome} - foto ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {/* Box contatti */}
          <div className="bg-night-light rounded-xl p-6 border border-gray-800">
            <h3 className="font-display text-xl text-white mb-4">Contatti</h3>
            <ul className="space-y-3">
              {locale.telefono && (
                <li className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href={`tel:${locale.telefono}`} className="hover:text-gold transition-colors">
                    {locale.telefono}
                  </a>
                </li>
              )}
              {locale.email && (
                <li className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href={`mailto:${locale.email}`} className="hover:text-gold transition-colors truncate">
                    {locale.email}
                  </a>
                </li>
              )}
              {locale.sitoWeb && (
                <li className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <a
                    href={locale.sitoWeb}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold transition-colors truncate"
                  >
                    Sito web
                  </a>
                </li>
              )}
              {locale.instagram && (
                <li className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-gold flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                  </svg>
                  <a
                    href={`https://instagram.com/${locale.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold transition-colors"
                  >
                    {locale.instagram}
                  </a>
                </li>
              )}
              {locale.facebook && (
                <li className="flex items-center gap-3 text-gray-300">
                  <svg className="w-5 h-5 text-gold flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <a
                    href={`https://facebook.com/${locale.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold transition-colors"
                  >
                    Facebook
                  </a>
                </li>
              )}
            </ul>

            {/* Indirizzo completo e bottone navigazione */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-sm text-gray-400 mb-3">
                {locale.indirizzo}
                <br />
                {locale.cap} {locale.comune} ({locale.provincia})
              </p>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${locale.latitudine},${locale.longitudine}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full text-center block"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Indicazioni
                </span>
              </a>
            </div>
          </div>

          {/* Box orari */}
          {locale.orari && (
            <div className="bg-night-light rounded-xl p-6 border border-gray-800">
              <h3 className="font-display text-xl text-white mb-4">Orari</h3>
              <ul className="space-y-2 text-sm">
                {giorniSettimana.map(({ key, label }) => {
                  const orario = locale.orari?.[key]
                  const isChiuso = orario?.toLowerCase() === 'chiuso'
                  return (
                    <li key={key} className="flex justify-between">
                      <span className="text-gray-400">{label}</span>
                      <span className={isChiuso ? 'text-gray-500' : 'text-gray-300'}>
                        {orario || '-'}
                      </span>
                    </li>
                  )
                })}
              </ul>
              {locale.orari.note && (
                <p className="mt-4 pt-4 border-t border-gray-700 text-sm text-gold">
                  {locale.orari.note}
                </p>
              )}
            </div>
          )}

          {/* Data approvazione */}
          {locale.dataApprovazione && (
            <div className="text-center text-sm text-gray-500">
              <p>
                Approvato il{' '}
                {new Date(locale.dataApprovazione).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
          )}
        </aside>
      </div>
    </article>
  )
}
